import { useState } from 'react';
import type { Vehicle } from '@/data/siteData';
import { Link } from 'react-router-dom';
import VehiclePrice from '@/components/feature/VehiclePrice';

interface VehicleDetailProps {
  vehicle: Vehicle;
}

export default function VehicleDetail({ vehicle }: VehicleDetailProps) {
  const [activeImage, setActiveImage] = useState(0);

  const keyFacts = [
    { label: 'Année', value: vehicle.year.toString(), icon: 'ri-calendar-line' },
    { label: 'Kilométrage', value: `${vehicle.mileage.toLocaleString('fr-FR')} km`, icon: 'ri-speed-up-line' },
    { label: 'Carburant', value: vehicle.fuel, icon: 'ri-gas-station-line' },
    { label: 'Boîte de vitesses', value: vehicle.gearbox, icon: 'ri-settings-4-line' },
  ];

  const characteristics = vehicle.specs;

  return (
    <div className="bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10 py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
          {/* Gallery */}
          <div>
            <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-background-200 border border-background-200">
              {vehicle.photos[activeImage] ? (
                <img loading="lazy" decoding="async"
                  src={vehicle.photos[activeImage]}
                  alt={`${vehicle.brand} ${vehicle.model} ${vehicle.motorisation}`}
                  className="w-full h-full object-cover object-bottom"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-foreground-400 text-sm">
                  Photo à venir
                </div>
              )}
            </div>
            {vehicle.photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
                {vehicle.photos.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`relative aspect-[4/3] overflow-hidden rounded-md border-2 transition-all cursor-pointer ${
                      activeImage === idx ? 'border-primary-500' : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                    aria-label={`Photo ${idx + 1}`}
                  >
                    <img loading="lazy" decoding="async" src={img} alt="" className="w-full h-full object-cover object-bottom" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs md:text-sm font-medium text-secondary-900">
              <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center text-secondary-500" />
              {vehicle.location}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">
              {vehicle.brand} {vehicle.model}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base mt-1">{vehicle.motorisation}</p>

            <VehiclePrice price={vehicle.price} promoPrice={vehicle.promoPrice} variant="detail" />

            {/* Key facts */}
            <div className="grid grid-cols-2 gap-3 mt-6">
              {keyFacts.map((fact) => (
                <div key={fact.label} className="flex items-center gap-3 rounded-lg border border-background-200 bg-white p-3">
                  <div className="w-9 h-9 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                    <i className={`${fact.icon} w-5 h-5 flex items-center justify-center`} />
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">{fact.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{fact.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <a
                href="tel:+262692505551"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-white font-label font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap"
              >
                <i className="ri-phone-line w-4 h-4 flex items-center justify-center" />
                Appelez-nous
              </a>
              <a
                href={`https://wa.me/262692505551?text=${encodeURIComponent(`Bonjour, je suis intéressé par le ${vehicle.brand} ${vehicle.model} ${vehicle.motorisation}.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-green-500 text-white font-label font-semibold text-sm hover:bg-green-600 transition-all whitespace-nowrap"
              >
                <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center" />
                Écrivez-nous
              </a>
            </div>
          </div>
        </div>

        {/* Description / Caractéristiques / Équipements — trois blocs séparés */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mt-10 md:mt-12">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg border border-background-200 p-5 md:p-6">
              <h2 className="font-heading font-bold text-base md:text-lg text-foreground-900 mb-3">
                Description
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed whitespace-pre-line">{vehicle.description}</p>
            </div>

            <div className="bg-white rounded-lg border border-background-200 p-5 md:p-6">
              <h2 className="font-heading font-bold text-base md:text-lg text-foreground-900 mb-4">
                Équipements
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {vehicle.equipment.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <div className="bg-white rounded-lg border border-background-200 p-5 md:p-6">
              <h2 className="font-heading font-bold text-base md:text-lg text-foreground-900 mb-4">
                Caractéristiques
              </h2>
              <dl className="space-y-3">
                {characteristics.map((spec) => (
                  <div key={spec.label} className="flex items-center justify-between gap-4 border-b border-background-100 pb-2 last:border-0 last:pb-0">
                    <dt className="text-sm text-foreground-500">{spec.label}</dt>
                    <dd className="text-sm font-medium text-foreground-900 text-right">{spec.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>

        {/* Retour à la liste des véhicules */}
        <div className="flex justify-center mt-10 md:mt-12">
          <Link
            to="/vehicules"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-foreground-300 text-foreground-700 font-label font-medium text-sm hover:border-primary-500 hover:text-primary-500 transition-all whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
            Voir tous nos véhicules
          </Link>
        </div>
      </div>
    </div>
  );
}